/**
 * The ribbon: the "Menu" button, the ribbon TABS (one per top-level folder, a
 * "Home" tab for top-level screens, and View), and the selected tab's
 * commands as GROUPS of large buttons.
 *
 * Any depth, no two-level shortcut: a tab's direct screens form its first
 * group and each sub-folder is a group of its own; inside a group a screen is
 * a button, a folder is a drop-down button whose CascadeMenu reaches every
 * level below it, and a website (a screen with pages) is a split button —
 * the top half opens it, the arrow drops its pages.
 *
 * Keyboard: the tabs are a WAI-ARIA tablist (Left / Right / Home / End,
 * automatic activation); the commands are a toolbar (one tab stop, Left /
 * Right / Home / End between its controls, Down / Enter / Space opens a
 * drop-down). Right-click or Shift+F10 on a screen button offers Pin / Unpin.
 */
import { ChevronDown, ChevronUp, House, Maximize2, PanelsTopLeft, SquareX, AppWindow } from 'lucide-react'
import {
  useCallback, useEffect, useId, useRef, useState,
  type CSSProperties, type KeyboardEvent, type ReactNode, type RefObject,
} from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../../nav/navIcons'
import { CascadeMenu } from '../../nav/parts/Menus'
import { useEntryMenu } from '../../nav/parts/ShellLists'
import { S, iconButton } from '../../nav/tokens'
import type { NavTreeNode } from '../../nav/tree'
import type { ShellNavModel } from '../../nav/useShellNav'
import { ACCENT_SOLID, IconTip, moveFocus, useOutsideDown } from './shared'
import { SwitchWindowsMenu } from './SwitchWindows'

export const HOME_TAB = 'home'
export const VIEW_TAB = 'view'

export interface RibbonTab {
  id: string
  label: string
  /** The top-level folder this tab shows (absent for Home / View). */
  node?: NavTreeNode
}

interface Group { id: string; label: string; nodes: NavTreeNode[]; empty: boolean }

/** The groups a tab shows: its direct screens, then one group per sub-folder. */
function groupsOf(tab: RibbonTab, model: ShellNavModel): Group[] {
  if (tab.id === HOME_TAB) {
    const nodes = model.roots.filter((n) => !n.isFolder)
    return [{ id: HOME_TAB, label: 'Screens', nodes, empty: nodes.length === 0 }]
  }
  const f = tab.node
  if (!f) return []
  const out: Group[] = []
  const direct = f.children.filter((c) => !c.isFolder)
  if (direct.length || f.children.length === 0) {
    out.push({ id: f.item.id, label: f.item.title, nodes: direct, empty: f.children.length === 0 })
  }
  for (const sub of f.children.filter((c) => c.isFolder)) {
    out.push({ id: sub.item.id, label: sub.item.title, nodes: sub.children, empty: sub.children.length === 0 })
  }
  return out
}

export interface RibbonProps {
  model: ShellNavModel
  tabs: RibbonTab[]
  activeTab: string
  onSelectTab: (id: string) => void
  collapsed: boolean
  onCollapsed: (on: boolean) => void
  statusOn: boolean
  onStatus: (on: boolean) => void
  menuOpen: boolean
  onOpenMenu: (from: HTMLElement) => void
  menuButtonRef: RefObject<HTMLButtonElement | null>
  emptyNavMessage: string
  compact: boolean
}

type PopupSpec =
  | { kind: 'menu'; key: string; label: string; nodes: NavTreeNode[] }
  | { kind: 'switch'; key: string }
type Popup = PopupSpec & { anchor: HTMLElement; left: number; top: number }

export function Ribbon({
  model, tabs, activeTab, onSelectTab, collapsed, onCollapsed, statusOn, onStatus,
  menuOpen, onOpenMenu, menuButtonRef, emptyNavMessage, compact,
}: RibbonProps) {
  const uid = useId()
  const tabId = (id: string) => `${uid}-tab-${id}`
  const panelId = `${uid}-panel`
  const tab = tabs.find((t) => t.id === activeTab) || tabs[0]
  const entryMenu = useEntryMenu(model)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [popup, setPopup] = useState<Popup | null>(null)
  const closePopup = useCallback(() => setPopup(null), [])
  useOutsideDown(!!popup, (t) => !!popRef.current?.contains(t) || !!popup?.anchor.contains(t), closePopup)
  // A pop-up belongs to the tab it was opened from.
  useEffect(() => { setPopup(null) }, [activeTab, collapsed])

  const openPopupAt = (anchor: HTMLElement, p: PopupSpec) => {
    const r = anchor.getBoundingClientRect()
    const width = p.kind === 'switch' ? 340 : 280
    setPopup({ ...p, anchor, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), top: r.bottom + 2 })
  }

  // ── Tabs ────────────────────────────────────────────────────────────
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')]
    const el = moveFocus(els, e.key)
    if (!el) return
    e.preventDefault()
    const id = el.dataset.tab
    if (id) onSelectTab(id)
  }

  // ── Toolbar: roving tab stop over every control (`data-rb`) ─────────
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const keys: string[] = []
  const rb = (key: string) => {
    const first = keys.length === 0
    keys.push(key)
    return {
      'data-rb': key,
      tabIndex: (focusKey ? focusKey === key : first) ? 0 : -1,
      onFocus: () => setFocusKey(key),
    }
  }
  // A remembered stop that is no longer drawn (another tab) gives way to the first.
  useEffect(() => {
    if (focusKey && !keys.includes(focusKey)) setFocusKey(null)
  })
  const toolbarEls = () => [...(toolbarRef.current?.querySelectorAll<HTMLElement>('[data-rb]') || [])]
    .filter((el) => !(el as HTMLButtonElement).disabled)
  const onToolbarKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (moveFocus(toolbarEls(), e.key)) e.preventDefault()
  }
  /** Left / Right on an open drop-down's top level: close it and move along the toolbar. */
  const sideFrom = (anchor: HTMLElement, dir: -1 | 1) => {
    setPopup(null)
    const els = toolbarEls()
    const i = els.indexOf(anchor)
    els[(i + dir + els.length) % els.length]?.focus()
  }

  const small = compact
  const bigBtn = (on: boolean, open: boolean): CSSProperties => ({
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    width: small ? 80 : 88, minHeight: small ? 64 : 76, padding: '6px 4px',
    border: `1px solid ${on ? S.accent : 'transparent'}`, borderRadius: 6,
    background: on ? S.accentWeak : open ? S.surface2 : 'transparent',
    color: S.text, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center',
  })
  const label = (text: string) => (
    <span className="tw-oc-label" style={{ fontSize: 13, lineHeight: 1.25, fontWeight: 400, maxWidth: '100%' }}>{text}</span>
  )

  const screenButton = (n: NavTreeNode) => {
    const { item } = n
    const on = model.current?.id === item.id
    return (
      <button key={item.id} type="button" className="tw-oc-rb" {...rb(`s:${item.id}`)}
        onClick={() => model.open(item)}
        onContextMenu={entryMenu.onContextMenu(item)}
        onKeyDown={entryMenu.onKeyDown(item)}
        aria-current={on ? 'page' : undefined}
        {...tw('nav', item.obj_name, { type: item.obj_type, folder: false, selected: on })}
        style={bigBtn(on, false)}>
        <NavIcon node={item} size={small ? 24 : 28} strokeWidth={1.5} color={S.accent} />
        {label(item.title)}
      </button>
    )
  }

  const folderButton = (n: NavTreeNode) => {
    const { item } = n
    const key = `f:${item.id}`
    const open = popup?.kind === 'menu' && popup.key === key
    const toggle = (anchor: HTMLElement) => {
      if (open) { setPopup(null); return }
      openPopupAt(anchor, { kind: 'menu', key, label: item.title, nodes: n.children })
    }
    return (
      <button key={item.id} type="button" className="tw-oc-rb" {...rb(key)}
        aria-haspopup="menu" aria-expanded={open}
        onClick={(e) => toggle(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (!open) openPopupAt(e.currentTarget, { kind: 'menu', key, label: item.title, nodes: n.children })
          }
        }}
        {...tw('nav', item.obj_name || item.title, { type: item.obj_type, folder: true, expanded: open })}
        style={bigBtn(false, open)}>
        <NavIcon node={item} size={small ? 24 : 28} strokeWidth={1.5} color={S.accent} />
        {label(item.title)}
        <ChevronDown size={12} strokeWidth={2.6} aria-hidden color={S.text2} />
      </button>
    )
  }

  /** A screen with pages of its own (a website): open it, or drop its pages. */
  const splitButton = (n: NavTreeNode) => {
    const { item } = n
    const key = `sa:${item.id}`
    const on = model.current?.id === item.id
    const open = popup?.kind === 'menu' && popup.key === key
    const drop = (anchor: HTMLElement) => openPopupAt(anchor, { kind: 'menu', key, label: item.title, nodes: n.children })
    return (
      <div key={item.id} style={{
        display: 'flex', flexDirection: 'column', width: small ? 80 : 88, borderRadius: 6,
        border: `1px solid ${on ? S.accent : 'transparent'}`, background: on ? S.accentWeak : 'transparent',
      }}>
        <button type="button" className="tw-oc-rb" {...rb(`s:${item.id}`)}
          onClick={() => model.open(item)}
          onContextMenu={entryMenu.onContextMenu(item)}
          onKeyDown={entryMenu.onKeyDown(item)}
          aria-current={on ? 'page' : undefined}
          {...tw('nav', item.obj_name, { type: item.obj_type, folder: false, selected: on })}
          style={{ ...bigBtn(false, false), width: '100%', minHeight: small ? 46 : 56, border: 'none', borderRadius: '6px 6px 0 0' }}>
          <NavIcon node={item} size={small ? 22 : 26} strokeWidth={1.5} color={S.accent} />
          {label(item.title)}
        </button>
        <button type="button" className="tw-oc-rb" {...rb(key)}
          aria-haspopup="menu" aria-expanded={open} aria-label={`Pages in ${item.title}`}
          onClick={(e) => { if (open) setPopup(null); else drop(e.currentTarget) }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (!open) drop(e.currentTarget)
            }
          }}
          {...tw('action', 'shell-office-split', { obj: item.obj_name, expanded: open })}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 20,
            padding: 0, border: 'none', borderRadius: '0 0 6px 6px', background: open ? S.surface2 : 'transparent',
            color: S.text2, cursor: 'pointer',
          }}>
          <ChevronDown size={12} strokeWidth={2.6} aria-hidden />
        </button>
      </div>
    )
  }

  const nodeButton = (n: NavTreeNode) => {
    if (n.isFolder) return folderButton(n)
    if (n.children.length) return splitButton(n)
    return screenButton(n)
  }

  const group = (id: string, groupLabel: string, body: ReactNode) => (
    <div key={id} role="group" aria-label={groupLabel} style={{
      display: 'flex', flexDirection: 'column', minHeight: small ? 84 : 100, maxWidth: '100%',
      padding: '0 8px', borderRight: `1px solid ${S.border}`,
    }}>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>{body}</div>
      <div aria-hidden className="tw-sh-clip" style={{
        padding: '3px 0 6px', textAlign: 'center', fontSize: 13, color: S.text2, maxWidth: '100%',
      }}>{groupLabel}</div>
    </div>
  )

  const emptyNote = (text: string) => (
    <p style={{ margin: '8px 6px', maxWidth: 260, fontSize: 13.5, lineHeight: 1.45, color: S.text2 }}>{text}</p>
  )

  // ── View tab ────────────────────────────────────────────────────────
  const editorsCount = model.editorsEnabled ? model.editors.length : 0
  const swOpen = popup?.kind === 'switch'
  const viewGroups = (
    <>
      {group('v-screen', 'Screen', (
        <button type="button" className="tw-oc-rb" {...rb('v:full')}
          disabled={!model.current}
          onClick={() => model.setFullScreen(true)}
          {...tw('action', 'shell-office-fullscreen')}
          style={{ ...bigBtn(false, false), opacity: model.current ? 1 : 0.5, cursor: model.current ? 'pointer' : 'default' }}>
          <Maximize2 size={small ? 22 : 26} strokeWidth={1.5} aria-hidden color={S.accent} />
          {label('Full Screen')}
        </button>
      ))}
      {group('v-window', 'Window', (
        <>
          <button type="button" className="tw-oc-rb" {...rb('v:switch')}
            aria-haspopup="menu" aria-expanded={swOpen}
            aria-label={`Switch Windows, ${model.openScreens.length} open screens${model.editorsEnabled ? `, ${editorsCount} open editors` : ''}`}
            onClick={(e) => { if (swOpen) setPopup(null); else openPopupAt(e.currentTarget, { kind: 'switch', key: 'switch' }) }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                if (!swOpen) openPopupAt(e.currentTarget, { kind: 'switch', key: 'switch' })
              }
            }}
            {...tw('action', 'shell-office-switch-windows', { expanded: swOpen })}
            style={{ ...bigBtn(false, swOpen), position: 'relative', width: small ? 88 : 96 }}>
            <AppWindow size={small ? 22 : 26} strokeWidth={1.5} aria-hidden color={S.accent} />
            {label('Switch Windows')}
            <ChevronDown size={12} strokeWidth={2.6} aria-hidden color={S.text2} />
            {editorsCount > 0 && (
              <span aria-hidden style={{
                position: 'absolute', top: 4, right: 10, minWidth: 20, height: 20, padding: '0 5px',
                borderRadius: 10, background: ACCENT_SOLID, color: '#fff', fontSize: 13, fontWeight: 700,
                lineHeight: '20px', textAlign: 'center',
              }}>{editorsCount}</span>
            )}
          </button>
          <button type="button" className="tw-oc-rb" {...rb('v:closeall')}
            disabled={!model.openScreens.length}
            onClick={() => model.closeAllScreens()}
            {...tw('action', 'shell-office-close-all')}
            style={{ ...bigBtn(false, false), opacity: model.openScreens.length ? 1 : 0.5, cursor: model.openScreens.length ? 'pointer' : 'default' }}>
            <SquareX size={small ? 22 : 26} strokeWidth={1.5} aria-hidden color={S.accent} />
            {label('Close All')}
          </button>
        </>
      ))}
      {group('v-show', 'Show', (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6, minHeight: small ? 64 : 76, padding: '0 4px' }}>
          {([
            ['collapse', 'Collapse ribbon', collapsed, onCollapsed],
            ['status', 'Status bar', statusOn, onStatus],
          ] as const).map(([k, text, checked, set]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32, fontSize: 14, color: S.text, cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} onChange={(e) => set(e.target.checked)}
                {...rb(`v:${k}`)} {...tw('action', `shell-office-show-${k}`, { checked })}
                style={{ width: 16, height: 16, margin: 0, accentColor: S.accent, cursor: 'pointer' }} />
              {text}
            </label>
          ))}
        </div>
      ))}
    </>
  )

  // ── Content of the selected tab ─────────────────────────────────────
  let content: ReactNode
  if (!tab) content = null
  else if (tab.id === VIEW_TAB) content = viewGroups
  else if (tab.id === HOME_TAB && model.roots.length === 0) content = emptyNote(emptyNavMessage)
  else {
    content = groupsOf(tab, model).map((g) => group(g.id, g.label, g.empty
      ? emptyNote('No screens in this folder yet.')
      : g.nodes.map(nodeButton)))
  }

  return (
    <div style={{ position: 'relative', zIndex: 6, flexShrink: 0 }}>
      {/* Tab row */}
      <div style={{
        display: 'flex', alignItems: 'stretch', height: 40, background: S.panel,
        borderBottom: `1px solid ${S.border}`,
      }}>
        <button ref={menuButtonRef} type="button" className="tw-oc-solid"
          aria-haspopup="dialog" aria-expanded={menuOpen}
          onClick={(e) => onOpenMenu(e.currentTarget)}
          {...tw('action', 'shell-office-menu')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0 18px', border: 'none',
            background: ACCENT_SOLID, color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
          }}>
          Menu
        </button>
        <div role="tablist" aria-label="Ribbon" onKeyDown={onTabKey} style={{
          display: 'flex', alignItems: 'stretch', flex: 1, minWidth: 0, overflowX: 'auto', paddingLeft: 4,
        }}>
          {tabs.map((t) => {
            const on = t.id === tab?.id
            const lit = on && !collapsed
            return (
              <button key={t.id} type="button" role="tab" id={tabId(t.id)} data-tab={t.id}
                aria-selected={on} aria-controls={collapsed ? undefined : panelId}
                tabIndex={on ? 0 : -1}
                className="tw-oc-tab"
                onClick={() => { onSelectTab(t.id); if (collapsed) onCollapsed(false) }}
                {...tw('action', `shell-office-tab:${t.id}`, { selected: on, label: t.label })}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '0 14px', flexShrink: 0,
                  border: 'none', borderBottom: `3px solid ${lit ? S.accent : 'transparent'}`,
                  borderTop: '3px solid transparent', background: 'transparent',
                  color: lit ? S.accent : S.text, fontSize: 14, fontWeight: lit ? 600 : 400,
                  fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                {t.id === HOME_TAB ? <House size={15} aria-hidden />
                  : t.id === VIEW_TAB ? <PanelsTopLeft size={15} aria-hidden />
                    : <NavIcon node={t.node?.item} size={15} />}
                {t.label}
              </button>
            )
          })}
        </div>
        {collapsed && (
          <IconTip label="Expand ribbon" align="right">
            <button type="button" className="tw-oc-plain" onClick={() => onCollapsed(false)}
              aria-label="Expand ribbon" aria-expanded={false}
              {...tw('action', 'shell-office-ribbon-expand')}
              style={{ ...iconButton(40), borderRadius: 0, height: '100%' }}>
              <ChevronDown size={16} strokeWidth={2.2} aria-hidden />
            </button>
          </IconTip>
        )}
      </div>

      {/* Commands of the selected tab */}
      {!collapsed && tab && (
        <div id={panelId} role="tabpanel" aria-labelledby={tabId(tab.id)} style={{
          position: 'relative', background: S.panel, borderBottom: `1px solid ${S.border}`,
        }}>
          <div ref={toolbarRef} role="toolbar" aria-label={`${tab.label} commands`} onKeyDown={onToolbarKey}
            style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', rowGap: 4,
              minHeight: small ? 90 : 106, padding: '6px 48px 0 6px', boxSizing: 'border-box',
            }}>
            {content}
            <span style={{ position: 'absolute', right: 8, bottom: 6 }}>
              <IconTip label="Collapse ribbon" place="above" align="right">
                <button type="button" className="tw-oc-plain" {...rb('collapse')}
                  onClick={() => onCollapsed(true)}
                  aria-label="Collapse ribbon" aria-expanded
                  {...tw('action', 'shell-office-ribbon-collapse')}
                  style={iconButton(32)}>
                  <ChevronUp size={16} strokeWidth={2.2} aria-hidden />
                </button>
              </IconTip>
            </span>
          </div>
        </div>
      )}

      {/* Drop-downs: outside the toolbar so their own arrow keys stay theirs. */}
      {popup && (
        <div ref={popRef} data-tw-shell-menu="" style={{ position: 'fixed', left: popup.left, top: popup.top, zIndex: 9441 }}>
          {popup.kind === 'menu' ? (
            <CascadeMenu model={model} nodes={popup.nodes} label={popup.label}
              onPicked={closePopup}
              onCloseTop={() => { const a = popup.anchor; setPopup(null); a.focus() }}
              onSideKey={(dir) => sideFrom(popup.anchor, dir)} />
          ) : (
            <SwitchWindowsMenu model={model}
              onPicked={closePopup}
              onClose={(refocus) => { const a = popup.anchor; setPopup(null); if (refocus) a.focus() }} />
          )}
        </div>
      )}
      {entryMenu.ui}
    </div>
  )
}
